import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getToken } from '../services/tokenService';
import { computeTtcFromHtAndTaxes, normalizeTaxesSource } from '../lib/computeTtc';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useAvenantWorkflow() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const initialState = location.state || {};

  // États du workflow
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [avenantId] = useState(initialState.avenant_id || null);
  const [avenantRef] = useState(initialState.avenant_reference || null);
  const [contratParent] = useState(initialState.contrat_parent || null);
  const [tarification] = useState(initialState.tarification || null);

  // État SMS
  const [telephone, setTelephone] = useState(contratParent?.telephone || '');
  const [otpCode, setOtpCode] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  // État signature & paiement
  const [signed, setSigned] = useState(false);
  const [signedAt, setSignedAt] = useState(null);
  const [paymentDeclared, setPaymentDeclared] = useState(false);

  // État PDF
  const [draftPdfBlobUrl, setDraftPdfBlobUrl] = useState(null);
  const [devisPdfBlobUrl, setDevisPdfBlobUrl] = useState(null);
  const [, setPdfLoading] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);

  // Charger le PDF en blob
  const loadPdfAsBlob = async (url, setStateFn = setDraftPdfBlobUrl) => {
    try {
      setPdfLoading(true);
      const token = getToken();
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erreur chargement PDF');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setStateFn(blobUrl);
    } catch {
    } finally {
      setPdfLoading(false);
    }
  };

  // Générer automatiquement le DRAFT
  const autoGenerateDraft = async () => {
    if (!avenantId || autoGenerating) return;
    setAutoGenerating(true);
    try {
      const token = getToken();
      const response = await axios.post(
        `${API}/avenants/${avenantId}/generate-draft`,
        { include_bank_info: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        await loadPdfAsBlob(`${API}/avenants/${avenantId}/draft`, setDraftPdfBlobUrl);
        if (response.data.devis_path) {
          await loadPdfAsBlob(`${API}/avenants/${avenantId}/devis`, setDevisPdfBlobUrl);
        }
        toast.success('Documents générés avec succès');
      }
    } catch {
    } finally {
      setAutoGenerating(false);
    }
  };

  // Charger au montage
  useEffect(() => {
    if (avenantId && !draftPdfBlobUrl && !autoGenerating) {
      autoGenerateDraft();
    }
    return () => {
      if (draftPdfBlobUrl) URL.revokeObjectURL(draftPdfBlobUrl);
      if (devisPdfBlobUrl) URL.revokeObjectURL(devisPdfBlobUrl);
    };
    // eslint-disable-next-line
  }, [avenantId]);

  // Countdown SMS
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // Envoyer SMS
  const sendSMS = async () => {
    if (!telephone) {
      toast.error('Numéro de téléphone requis');
      return;
    }
    setLoading(true);
    try {
      const token = getToken();
      await axios.post(
        `${API}/avenants/${avenantId}/send-sms`,
        { telephone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Code envoyé !');
      setSmsSent(true);
      setResendCountdown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur envoi SMS');
    } finally {
      setLoading(false);
    }
  };

  // Vérifier SMS
  const verifySMS = async () => {
    if (otpCode.length !== 6) return;
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.post(
        `${API}/avenants/${avenantId}/verify-sms`,
        { code: otpCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.verified) {
        toast.success('Code vérifié !');
        setSmsVerified(true);
        setCurrentStep(2);
      } else {
        toast.error(response.data.message);
      }
    } catch {
      toast.error('Erreur vérification');
    } finally {
      setLoading(false);
    }
  };

  // Signer
  const signAvenant = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.post(
        `${API}/avenants/${avenantId}/sign`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success('Avenant signé !');
        setSigned(true);
        setSignedAt(response.data.signed_at);
        setCurrentStep(3);
      }
    } catch {
      toast.error('Erreur signature');
    } finally {
      setLoading(false);
    }
  };

  // Déclarer paiement
  const declarePayment = async () => {
    setLoading(true);
    try {
      const token = getToken();
      await axios.post(
        `${API}/avenants/${avenantId}/declare-payment`,
        { payment_reference: `VIRT-${avenantRef}` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Paiement déclaré');
      setPaymentDeclared(true);
    } catch {
      toast.error('Erreur déclaration');
    } finally {
      setLoading(false);
    }
  };

  // Copier
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text.replace(/\s/g, ''));
    toast.success(`${label} copié !`);
  };

  // Navigation
  const goToNextStep = () => setCurrentStep(Math.min(currentStep + 1, 4));
  const goToDashboard = () => navigate(user?.role === 'super_admin' ? '/super-admin' : '/cooper');

  // Montant — V37.34 : utilise l'utilitaire centralisee si tarification fournit
  // les taxes detaillees, sinon fallback sur le TTC backend ou HT × 1.085.
  const montantHT = tarification?.montant_ht || tarification?.montant_total_ht || 0;
  let montantTTC;
  if (tarification?.montant_ttc) {
    montantTTC = tarification.montant_ttc;
  } else {
    const taxesSource = normalizeTaxesSource(null, tarification?.taxes);
    montantTTC = computeTtcFromHtAndTaxes(montantHT, taxesSource, 0.085).ttc;
  }

  return {
    navigate, initialState,
    currentStep, setCurrentStep, loading,
    avenantId, avenantRef, contratParent, tarification,
    telephone, setTelephone, otpCode, setOtpCode, smsSent, smsVerified, resendCountdown,
    signed, signedAt, paymentDeclared,
    draftPdfBlobUrl, devisPdfBlobUrl, autoGenerating,
    sendSMS, verifySMS, signAvenant, declarePayment, copyToClipboard,
    goToNextStep, goToDashboard,
    montantHT, montantTTC, API,
  };
}
